/**
 * The Facebook click identifiers, kept from the arrival until the purchase.
 *
 * wiswiz #124 defect 3. Meta ties a sale to an advertisement through `fbc`,
 * the click identifier that rides on the ad link as `?fbclid=...`. Our
 * Purchase event is fired server-side, from the Mollie webhook, which has no
 * browser and no cookies — so between 20 July and 3 September Meta received
 * 57 purchases with a hashed email and nothing else, and the parent campaign
 * reported zero sales.
 *
 * This module does two small things:
 *   1. On arrival it writes the `_fbc` cookie in Meta's own format when the
 *      URL carries an `fbclid` and there is no cookie yet, or the cookie
 *      holds a different click id — that is Meta's own documented rule. The
 *      Meta pixel does the same, but it loads through the tag manager, which
 *      is later than our first navigation and is not always allowed to run.
 *   2. It reads `_fbc` and `_fbp` back when the checkout starts, so they can
 *      travel to the server with the payment.
 *
 * Both cookies keep Meta's exact shape, because Meta drops anything else:
 *   _fbc = fb.<subdomain index>.<creation time in ms>.<fbclid>
 *   _fbp = fb.<subdomain index>.<creation time in ms>.<random number>
 *
 * Privacy: dit is dezelfde cookie, met dezelfde waarde en dezelfde levensduur,
 * die de Meta-pixel zelf al schrijft bij hetzelfde bezoek — gemeten op de live
 * pagina in WebKit op 2 sep 2026: fbq bestaat en _fbc en _fbp staan er dan al.
 * Er komt dus geen nieuwe soort gegevens bij en geen nieuwe ontvanger. Er is in
 * deze code geen toestemmingsbanner die de pixel afschermt; de privacyverklaring
 * noemt er wel een. Dat is een vraag voor Philip, geen reden om dit pad anders
 * te behandelen dan de pixel ernaast.
 */

export const FBC_COOKIE = "_fbc";
const FBP_COOKIE = "_fbp";
/* Meta's own window for a click identifier is 90 days. */
const BEWAARTIJD_SEC = 90 * 24 * 60 * 60;

function leesCookie(naam: string): string | null {
  if (typeof document === "undefined") return null;
  /* document.cookie kan zelf gooien (een iframe zonder toegang, een browser die
     opslag blokkeert), en decodeURIComponent gooit URIError op een kapot
     percent-teken dat iets anders achterliet. Dit draait op ELKE navigatie van
     de site en bij het afrekenen: meten mag nooit blokkeren, dus een onleesbare
     cookie is gewoon 'geen cookie'. */
  try {
    const deel = document.cookie
      .split("; ")
      .find((c) => c.startsWith(naam + "="));
    if (!deel) return null;
    const waarde = deel.slice(naam.length + 1);
    return waarde ? decodeURIComponent(waarde) : null;
  } catch {
    return null;
  }
}

function schrijfCookie(naam: string, waarde: string): void {
  if (typeof document === "undefined") return;
  try {
    /* Op het hoofddomein, zodat wiswiz.nl en www.wiswiz.nl dezelfde klik delen.
       Een ECHTE domeincontrole, geen tekst-suffix: 'notwiswiz.nl' eindigt ook
       op die letters en de browser gooit de cookie dan helemaal weg. Geen
       Domain-attribuut op localhost: dat weigert de browser ook. */
    const host = window.location.hostname;
    const onsDomein = host === "wiswiz.nl" || host.endsWith(".wiswiz.nl");
    const domein = onsDomein ? "; domain=.wiswiz.nl" : "";
    const veilig = window.location.protocol === "https:" ? "; secure" : "";
    document.cookie =
      `${naam}=${encodeURIComponent(waarde)}; path=/; max-age=${BEWAARTIJD_SEC}` +
      `; samesite=lax${domein}${veilig}`;
  } catch {
    /* opslag geweigerd: dan schrijft de pixel hem, of niemand — nooit een fout */
  }
}

/**
 * Meta's eigen vormen — MET de extra delen die de pixel er zelf achter hangt.
 *
 * #137, gemeten op de live pagina op 3 sep 2026 in WebKit met de
 * Instagram-in-app-identiteit: wij schrijven bij aankomst
 * `_fbc=fb.1.<tijd>.<fbclid>` (vier delen), en daarna herschrijft
 * fbevents.js (2.9.390) diezelfde cookie naar
 * `fb.1.<tijd>.<fbclid>.AQYAAQIB` — een vijfde deel. `_fbp` krijgt hetzelfde
 * achtervoegsel. De oude vormen hier stonden vast op vier delen en waren
 * verankerd, dus vanaf de tweede pagina las `leesKlikIds()` beide cookies als
 * null en vertrok de afreken-POST met `fbc: null, fbp: null`. De cookie stond
 * er wel; wij weigerden hem zelf.
 *
 * Meta verwacht de cookiewaarde ONGEWIJZIGD terug, dus we sturen precies wat
 * er staat. De vorm laat daarom extra delen toe, maar blijft begrensd: een
 * waarde die hier niet op lijkt is niet van Meta en gaat niet mee.
 */
const FBC_VORM = /^fb\.[0-2]\.\d{10,16}\.[A-Za-z0-9_-]{1,400}(?:\.[A-Za-z0-9_-]{1,200}){0,3}$/;
const FBP_VORM = /^fb\.[0-2]\.\d{10,16}\.\d{1,30}(?:\.[A-Za-z0-9_-]{1,200}){0,3}$/;

/** De klik-id zelf uit een cookie in Meta's vorm: het VIERDE deel.
 *  Niet 'alles na het derde punt': dan telt het achtervoegsel van de pixel mee
 *  en is de klik-id nooit gelijk aan de fbclid uit de URL, waardoor we de
 *  verrijkte cookie van de pixel bij elke navigatie zouden overschrijven. */
function klikIdUit(fbcCookie: string): string {
  return fbcCookie.split(".")[3] ?? "";
}

/**
 * Bewaar de klik-id van deze aankomst — met Meta's eigen regel.
 *
 * Meta schrijft de regel voor in "ClickID and the fbp and fbc Parameters"
 * (developers.facebook.com/docs/marketing-api/conversions-api/parameters/
 * fbp-and-fbc/): schrijf de cookie ALLEEN als er nog geen `_fbc` is, OF als de
 * `fbclid` in de URL niet gelijk is aan de klik-id in de cookie. De laatste
 * klik telt dus, niet de eerste. De Meta-pixel doet naast ons hetzelfde; deed
 * deze module het anders, dan stond er bij een tweede advertentieklik een
 * andere `fbc` in ons afrekenen dan in de gebeurtenis van de pixel.
 *
 * Is de klik-id gelijk, dan schrijven we niets. Zo blijft de tijd van de
 * eerste klik staan, precies zoals Meta's regel bedoelt.
 */
export function bewaarKlikId(zoek?: string): void {
  if (typeof window === "undefined") return;
  const params = new URLSearchParams(zoek ?? window.location.search);
  const fbclid = params.get("fbclid");
  if (!fbclid) return;
  /* Aanwezig is niet hetzelfde als GELDIG: staat er een _fbc die niet Meta's
     vorm heeft, dan leest leesKlikIds() hem straks als null. Zo'n cookie mag
     de nieuwe klik-id nooit tegenhouden. */
  if (draagtKlikId(leesCookie(FBC_COOKIE) ?? undefined, fbclid)) return;
  if (!geldigeKlikId(fbclid)) return;
  schrijfCookie(FBC_COOKIE, bouwFbc(fbclid));
}

/** Is dit een klik-id die Meta zelf zou kunnen hebben geschreven? */
export function geldigeKlikId(fbclid: string): boolean {
  /* Meta's klik-id's worden langer met de jaren; ruim, maar niet grenzeloos. */
  return /^[A-Za-z0-9_-]{1,400}$/.test(fbclid);
}

/** Meta's cookievorm voor deze klik. */
export function bouwFbc(fbclid: string, nu: number = Date.now()): string {
  return `fb.1.${nu}.${fbclid}`;
}

/**
 * Draagt deze cookiewaarde al precies deze klik? Dan blijft hij staan.
 *
 * Zo overschrijven we nooit de cookie die de Meta-pixel zelf heeft verrijkt,
 * en blijft de tijd van de eerste klik staan — Meta's eigen regel.
 */
export function draagtKlikId(fbcCookie: string | undefined, fbclid: string): boolean {
  if (!fbcCookie) return false;
  return FBC_VORM.test(fbcCookie) && klikIdUit(fbcCookie) === fbclid;
}

/** De twee identifiers zoals de browser ze nu heeft; null als ze ontbreken. */
export function leesKlikIds(): { fbc: string | null; fbp: string | null } {
  const fbc = leesCookie(FBC_COOKIE);
  const fbp = leesCookie(FBP_COOKIE);
  return {
    fbc: fbc && FBC_VORM.test(fbc) ? fbc : null,
    fbp: fbp && FBP_VORM.test(fbp) ? fbp : null
  };
}

/* 3 sep (wiswiz #152, ketencheck #148 punt 3): de URL van de betaalpagina reist mee naar de backend, die hem als
   event_source_url op de server-side Purchase zet (Meta vraagt de pagina-URL bij elk event; zonder was de url-statistiek
   van Purchase leeg). Zonder hash; verder de URL zoals de pixel hem ook stuurt (met query). Nooit blokkerend: geen
   window (SSR) = null, en de backend controleert host en lengte. */
export function leesPaginaUrl(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const { origin, pathname, search } = window.location;
    return `${origin}${pathname}${search}`.slice(0, 2048);
  } catch {
    return null;
  }
}
